import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchEntries,
  createEntry,
  editEntry,
  createTimeEntryChangeRequest,
  deleteEntry,
  createSickDay,
} from '../slices/timeEntrySlice';
import type { RootState, AppDispatch } from '../store/store';
import CreateTimeEntryForm from './CreateTimeEntryForm';
import EditTimeEntryForm from './EditTimeEntryForm';
import { fetchAllUsers } from '../slices/authSlice';
import SickDayForm from './SickDayForm';
import { apiUrl } from '../config/api';

const getWeekNumber = (date: Date) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
};

const getMonday = (date: Date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

const getSunday = (date: Date) => {
  const monday = getMonday(date);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  sunday.setHours(23, 59, 59, 999);
  return sunday;
};

const getMonthName = (date: Date) =>
  date.toLocaleString('de-DE', { month: 'long' });

const formatDateDMY = (date: Date) =>
  `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}`;

const formatDateInputLocal = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const formatClockTime = (dateString?: string) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const formatDateTime = (dateString?: string) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatHoursMinutes = (hours: number) => {
  if (!hours || hours < 0) hours = 0;
  const totalMinutes = Math.round(hours * 60);
  const hh = Math.floor(totalMinutes / 60).toString().padStart(2, '0');
  const mm = (totalMinutes % 60).toString().padStart(2, '0');
  return `${hh}:${mm}`;
};

interface TimeEntryChangeRequest {
  _id: string;
  entryId: string | { _id: string };
  requestedStartTime: string;
  requestedEndTime: string;
  requestedPausedDuration: number;
  comment?: string;
  status: 'pending' | 'approved' | 'declined';
}

const getWeekInputValue = (date: Date): string => {
  const year = date.getFullYear();
  const week = getWeekNumber(date);
  return `${year}-W${String(week).padStart(2, '0')}`;
};

const parseWeekInputValue = (weekString: string): Date => {
  const [yearStr, weekStr] = weekString.split('-W');
  const year = Number(yearStr);
  const week = Number(weekStr);
  const jan4 = new Date(year, 0, 4);
  const jan4Day = jan4.getDay() || 7;
  const monday = new Date(jan4);
  monday.setDate(jan4.getDate() - jan4Day + 1 + (week - 1) * 7);
  monday.setHours(0, 0, 0, 0);
  return monday;
};

type TimeEntryTab = 'day' | 'week' | 'month';

const getAnchorDateFromInput = (dateString: string): Date => {
  if (dateString.includes('-W')) {
    return parseWeekInputValue(dateString);
  }

  if (/^\d{4}-\d{2}$/.test(dateString)) {
    const [year, month] = dateString.split('-').map(Number);
    return new Date(year, month - 1, 15);
  }

  return new Date(dateString);
};

const getDateValueForTab = (tab: TimeEntryTab, dateString: string): string => {
  const anchorDate = getAnchorDateFromInput(dateString);

  if (tab === 'day') {
    return formatDateInputLocal(anchorDate);
  }

  if (tab === 'week') {
    return getWeekInputValue(anchorDate);
  }

  return `${anchorDate.getFullYear()}-${String(anchorDate.getMonth() + 1).padStart(2, '0')}`;
};

const isTimeEntryTab = (value: string | null): value is TimeEntryTab =>
  value === 'day' || value === 'week' || value === 'month';

const getInitialTimeEntryTab = (): TimeEntryTab => {
  const savedFilter = localStorage.getItem('filter');
  return isTimeEntryTab(savedFilter) ? savedFilter : 'day';
};

const TimeEntryOverview = () => {
  const dispatch = useDispatch<AppDispatch>();
  const entries = useSelector((state: RootState) => state.time.entries);
  const loading = useSelector((state: RootState) => state.time.loading);
  const error = useSelector((state: RootState) => state.time.error);
  const user = useSelector((state: RootState) => state.auth.user);
  const token = useSelector((state: RootState) => state.auth.token);
  const allUsers = useSelector((state: RootState) => state.auth.allUsers || []);

  const userRole = user?.role;
  const userId = user?._id;
  const initialTab = getInitialTimeEntryTab();
  const initialDate = getDateValueForTab(initialTab, formatDateInputLocal(new Date()));

  const [activeTab, setActiveTab] = useState<TimeEntryTab>(initialTab);
  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [selectedUserId, setSelectedUserId] = useState(() => {
    return localStorage.getItem('timeEntriesUserId') || userId || '';
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSickForm, setShowSickForm] = useState(false);
  const [entryToEdit, setEntryToEdit] = useState<any>(null);
  const [changeRequests, setChangeRequests] = useState<TimeEntryChangeRequest[]>([]);
  const [reportSummary, setReportSummary] = useState<{
    overtime: number;
    undertime: number;
  } | null>(null);

  useEffect(() => {
    dispatch(fetchAllUsers());
  }, [dispatch]);

  useEffect(() => {
    localStorage.setItem('filter', activeTab);
    localStorage.setItem('date', selectedDate);
  }, [activeTab, selectedDate]);

  const fetchChangeRequests = useCallback(async () => {
    if (!token || !userId) return;

    try {
      const res = await fetch(apiUrl('/api/time-entry-change-requests'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      setChangeRequests(await res.json());
    } catch (err) {
      console.error('Fehler beim Laden der Zeitänderungsanträge:', err);
    }
  }, [token, userId]);

  useEffect(() => {
    fetchChangeRequests();
  }, [fetchChangeRequests]);

  useEffect(() => {
    if (userId && !selectedUserId) {
      setSelectedUserId(userId);
      localStorage.setItem('timeEntriesUserId', userId);
    }
  }, [userId, selectedUserId]);

  useEffect(() => {
    const targetUserId =
      userRole === 'admin' ? (selectedUserId || undefined) : (userId || undefined);

    let dateParam = selectedDate;

    if (activeTab === 'week' && !selectedDate.includes('-W')) {
      dateParam = getWeekInputValue(new Date(selectedDate));
    }

    if (activeTab === 'month' && selectedDate.includes('-W')) {
      const dateObj = parseWeekInputValue(selectedDate);
      dateParam = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}`;
    }

    dispatch(fetchEntries({ filter: activeTab, date: dateParam, userId: targetUserId }));
  }, [dispatch, activeTab, selectedDate, selectedUserId, userRole, userId]);

  useEffect(() => {
    const fetchReport = async () => {
      if (!token || !userId) return;

      try {
        const targetUserId = userRole === 'admin' ? (selectedUserId || userId) : userId;
        let url = '';

        if (activeTab === 'day') {
          const dayDate = selectedDate.includes('-W')
            ? formatDateInputLocal(parseWeekInputValue(selectedDate))
            : selectedDate;
          url = apiUrl(`/api/reports?filter=range&from=${dayDate}&to=${dayDate}&userId=${targetUserId}`);
        }

        if (activeTab === 'week') {
          const monday = selectedDate.includes('-W')
            ? parseWeekInputValue(selectedDate)
            : getMonday(new Date(selectedDate));
          const sunday = getSunday(monday);

          const fromDate = formatDateInputLocal(monday);
          const toDate = formatDateInputLocal(sunday);

          url = apiUrl(`/api/reports?filter=range&from=${fromDate}&to=${toDate}&userId=${targetUserId}`);
        }

        if (activeTab === 'month') {
          let monthDate = selectedDate;

          if (selectedDate.includes('-W')) {
            const dateObj = parseWeekInputValue(selectedDate);
            monthDate = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}`;
          } else if (/^\d{4}-\d{2}-\d{2}$/.test(selectedDate)) {
            const dateObj = new Date(selectedDate);
            monthDate = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}`;
          }

          url = apiUrl(`/api/reports?filter=month&date=${monthDate}&userId=${targetUserId}`);
        }

        if (!url) return;

        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.ok) {
          setReportSummary({ overtime: 0, undertime: 0 });
          return;
        }

        const data = await res.json();

        setReportSummary({
          overtime: Number(data.overtime) || 0,
          undertime: Number(data.undertime) || 0,
        });
      } catch (err) {
        console.error('Fehler beim Laden der Über-/Minusstunden:', err);
        setReportSummary({ overtime: 0, undertime: 0 });
      }
    };

    fetchReport();
  }, [activeTab, selectedDate, selectedUserId, userId, userRole, token]);

  const formatTime = (milliseconds: number) => {
    if (milliseconds < 0) milliseconds = 0;
    const totalSeconds = Math.floor(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600).toString().padStart(2, '0');
    const minutes = Math.floor((totalSeconds % 3600) / 60).toString().padStart(2, '0');
    const seconds = (totalSeconds % 60).toString().padStart(2, '0');
    return `${hours}:${minutes}:${seconds}`;
  };

  const getEntryDuration = (entry: any) => {
    const startTime = new Date(entry.startTime).getTime();
    const endTime = new Date(entry.endTime || Date.now()).getTime();

    const pauseMs =
      entry.type === 'work'
        ? entry.pausedDuration
          ? entry.pausedDuration * 1000
          : (entry.breakMinutes || 0) * 60 * 1000
        : 0;

    return Math.max(0, endTime - startTime - pauseMs);
  };

  const visibleEntries = entries || [];

  const getUserName = (id: string) => {
    const match = allUsers.find((u) => u._id === id);
    return match ? match.username : id;
  };

  const getAdminEditorName = (adminEditedBy: any) => {
    if (!adminEditedBy) return 'Admin';
    if (typeof adminEditedBy === 'object' && adminEditedBy.username) {
      return adminEditedBy.username;
    }
    if (typeof adminEditedBy === 'string') {
      return getUserName(adminEditedBy);
    }
    return 'Admin';
  };

  const getPendingChangeRequest = (entryId: string) =>
    changeRequests.find((request) => {
      const requestEntryId =
        request.entryId && typeof request.entryId === 'object'
          ? request.entryId._id
          : request.entryId;
      return requestEntryId === entryId && request.status === 'pending';
    });

  const getInputType = () => {
    if (activeTab === 'day') return 'date';
    if (activeTab === 'week') return 'week';
    if (activeTab === 'month') return 'month';
    return 'date';
  };

  const getInputValue = () => {
    if (activeTab === 'week') {
      if (selectedDate.includes('-W')) return selectedDate;
      return getWeekInputValue(new Date(selectedDate));
    }

    if (activeTab === 'month') {
      if (/^\d{4}-\d{2}$/.test(selectedDate)) return selectedDate;
      const dateObj = new Date(selectedDate);
      return `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}`;
    }

    return selectedDate;
  };

  const handlePeriodStep = (direction: -1 | 1) => {
    const anchorDate = getAnchorDateFromInput(getInputValue());
    const nextDate = new Date(anchorDate);

    if (activeTab === 'day') {
      nextDate.setDate(nextDate.getDate() + direction);
    }

    if (activeTab === 'week') {
      nextDate.setDate(nextDate.getDate() + direction * 7);
    }

    if (activeTab === 'month') {
      nextDate.setMonth(nextDate.getMonth() + direction);
    }

    const nextValue = getDateValueForTab(activeTab, formatDateInputLocal(nextDate));
    setSelectedDate(nextValue);
    localStorage.setItem('date', nextValue);
  };

  const getSelectedPeriodDates = () => {
    if (activeTab === 'week') {
      const monday = selectedDate.includes('-W')
        ? parseWeekInputValue(selectedDate)
        : getMonday(new Date(selectedDate));
      return {
        startDate: formatDateInputLocal(monday),
        endDate: formatDateInputLocal(getSunday(monday)),
      };
    }

    if (activeTab === 'month') {
      let year: number;
      let monthIndex: number;

      if (/^\d{4}-\d{2}$/.test(selectedDate)) {
        const [selectedYear, selectedMonth] = selectedDate.split('-').map(Number);
        year = selectedYear;
        monthIndex = selectedMonth - 1;
      } else if (selectedDate.includes('-W')) {
        const dateObj = parseWeekInputValue(selectedDate);
        year = dateObj.getFullYear();
        monthIndex = dateObj.getMonth();
      } else {
        const dateObj = new Date(selectedDate);
        year = dateObj.getFullYear();
        monthIndex = dateObj.getMonth();
      }

      return {
        startDate: formatDateInputLocal(new Date(year, monthIndex, 1)),
        endDate: formatDateInputLocal(new Date(year, monthIndex + 1, 0)),
      };
    }

    const dayDate = selectedDate.includes('-W')
      ? formatDateInputLocal(parseWeekInputValue(selectedDate))
      : selectedDate;

    return {
      startDate: dayDate,
      endDate: dayDate,
    };
  };

  const getCreateTimeDefaults = () => {
    const { startDate } = getSelectedPeriodDates();
    return {
      startTime: `${startDate}T08:00`,
      endTime: `${startDate}T17:00`,
    };
  };

  const getHeaderDateDisplay = () => {
    if (activeTab === 'day') return formatDateDMY(new Date(selectedDate));

    if (activeTab === 'week') {
      const monday = selectedDate.includes('-W')
        ? parseWeekInputValue(selectedDate)
        : getMonday(new Date(selectedDate));
      return (
        <>
          KW {getWeekNumber(monday)} ({formatDateDMY(monday)} – {formatDateDMY(getSunday(monday))})
        </>
      );
    }

    if (activeTab === 'month') {
      if (/^\d{4}-\d{2}$/.test(selectedDate)) {
        const [year, month] = selectedDate.split('-').map(Number);
        return getMonthName(new Date(year, month - 1, 1));
      }
      return getMonthName(new Date(selectedDate));
    }

    return '';
  };

  const handleCreate = async (data: {
    userId: string;
    startTime: string;
    endTime: string;
    pausedMinutes: number;
  }) => {
    try {
      await dispatch(
        createEntry({
          userId: data.userId,
          startTime: data.startTime,
          endTime: data.endTime,
          pausedDuration: data.pausedMinutes * 60,
        })
      ).unwrap();
      setShowCreateModal(false);

      if (userRole === 'admin') {
        setSelectedUserId(data.userId);
        localStorage.setItem('timeEntriesUserId', data.userId);
      }

      dispatch(fetchEntries({
        filter: activeTab,
        date: selectedDate,
        userId: userRole === 'admin' ? data.userId : userId,
      }));
    } catch {
      alert('Fehler beim Erstellen des Eintrags.');
    }
  };

  const handleEditClick = (entry: any) => {
    const pendingRequest = getPendingChangeRequest(entry._id);

    if (userRole !== 'admin' && pendingRequest) {
      setEntryToEdit({
        ...entry,
        startTime: pendingRequest.requestedStartTime,
        endTime: pendingRequest.requestedEndTime,
        pausedDuration: pendingRequest.requestedPausedDuration,
        adminEditComment: pendingRequest.comment || '',
        hasPendingChangeRequest: true,
      });
    } else {
      setEntryToEdit({ ...entry, hasPendingChangeRequest: false });
    }

    setShowEditModal(true);
  };

  const handleSaveEdit = async (data: {
    id: string;
    startTime: string;
    endTime: string;
    pausedDuration: number;
    adminEditComment?: string;
  }) => {
    try {
      if (userRole === 'admin') {
        await dispatch(editEntry(data)).unwrap();
      } else {
        await dispatch(
          createTimeEntryChangeRequest({
            entryId: data.id,
            startTime: data.startTime,
            endTime: data.endTime,
            pausedDuration: data.pausedDuration,
            comment: data.adminEditComment,
          })
        ).unwrap();
        await fetchChangeRequests();
        alert('Dein Änderungsantrag wurde gespeichert.');
      }
      setShowEditModal(false);
      setEntryToEdit(null);
      dispatch(fetchEntries({
        filter: activeTab,
        date: selectedDate,
        userId: userRole === 'admin' ? selectedUserId : userId
      }));
    } catch {
      alert('Fehler beim Speichern des Eintrags.');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Willst du den Eintrag wirklich löschen?')) return;
    try {
      await dispatch(deleteEntry(id)).unwrap();
      dispatch(fetchEntries({
        filter: activeTab,
        date: selectedDate,
        userId: userRole === 'admin' ? selectedUserId : userId
      }));
    } catch {
      alert('Fehler beim Löschen des Eintrags.');
    }
  };

  return (
    <>
      <h4 className="mb-3">{getHeaderDateDisplay()}</h4>

      {reportSummary && (
        <div className="time-summary-box">
          <div className="time-summary-content">
            <div className="time-summary-item">
              <strong>Überstunden:</strong> {formatHoursMinutes(reportSummary.overtime)}
            </div>
            <div className="time-summary-item">
              <strong>Minusstunden:</strong> {formatHoursMinutes(reportSummary.undertime)}
            </div>
          </div>
        </div>
      )}

      <div className="d-flex gap-2 mb-3 flex-wrap align-items-center">
        {['day', 'week', 'month'].map((tab) => (
          <button
            key={tab}
            className={`btn btn-sm ${activeTab === tab ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => {
              const nextTab = tab as TimeEntryTab;
              const newDate = getDateValueForTab(nextTab, selectedDate);
              setSelectedDate(newDate);
              localStorage.setItem('filter', nextTab);
              localStorage.setItem('date', newDate);
              setActiveTab(nextTab);
            }}
          >
            {tab === 'day' && 'Täglich'}
            {tab === 'week' && 'Wöchentlich'}
            {tab === 'month' && 'Monatlich'}
          </button>
        ))}

        <div className="period-navigation ms-2">
          <button
            type="button"
            className="btn btn-sm btn-outline-primary period-nav-button"
            onClick={() => handlePeriodStep(-1)}
            aria-label="Vorheriger Zeitraum"
            title="Vorheriger Zeitraum"
          >
            <i className="bi bi-chevron-left" aria-hidden="true"></i>
          </button>

          <input
            type={getInputType()}
            className="form-control form-control-sm period-date-input"
            value={getInputValue()}
            onChange={(e) => {
              setSelectedDate(e.target.value);
              localStorage.setItem('date', e.target.value);
            }}
          />

          <button
            type="button"
            className="btn btn-sm btn-outline-primary period-nav-button"
            onClick={() => handlePeriodStep(1)}
            aria-label="Nächster Zeitraum"
            title="Nächster Zeitraum"
          >
            <i className="bi bi-chevron-right" aria-hidden="true"></i>
          </button>
        </div>

        {userRole === 'admin' && (
          <select
            className="form-select form-select-sm ms-2"
            style={{ maxWidth: '200px' }}
            value={selectedUserId}
            onChange={(e) => {
              setSelectedUserId(e.target.value);
              localStorage.setItem('timeEntriesUserId', e.target.value);
            }}
          >
            {allUsers.map((u) => (
              <option key={u._id} value={u._id}>
                {u.username}
              </option>
            ))}
          </select>
        )}

        {userRole === 'admin' && (
          <>
            <button className="btn btn-success btn-sm ms-auto" onClick={() => setShowCreateModal(true)}>
              Neuer Eintrag
            </button>
            <button className="btn btn-danger btn-sm" onClick={() => setShowSickForm(true)}>
              Krankheitstag(e)
            </button>
          </>
        )}
      </div>

      <div className="card p-3 shadow-sm">
        {loading && <p>Lädt Zeiteinträge...</p>}
        {error && <div className="alert alert-danger">{error}</div>}
        {!loading && !error && visibleEntries.length === 0 && <p>Keine Einträge vorhanden.</p>}

        {!loading && !error && visibleEntries.length > 0 && (
          <div className="d-flex flex-column gap-2">
            {visibleEntries.map((entry) => {
              const isSick = entry.type === 'sick';
              const isVacation = entry.type === 'vacation';
              const isWork = !isSick && !isVacation;
              const pendingChangeRequest = getPendingChangeRequest(entry._id);

              const duration = getEntryDuration(entry);

              return (
                <div
                  key={entry._id}
                  className={`entry-card p-2 rounded ${
                    isSick
                      ? 'entry-sick'
                      : isVacation
                      ? 'entry-vacation'
                      : 'bg-dark text-white'
                  }`}
                >
                  {userRole === 'admin' && (
                    <div className="mb-1 fw-bold small text-muted">
                      {getUserName(entry.userId)}
                    </div>
                  )}

                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <strong>
                        {isSick && 'Krank'}
                        {isVacation && 'Urlaub'}
                        {isWork && 'Gearbeitet'}
                      </strong>
                      <br />
                      Datum: {formatDateDMY(new Date(entry.startTime))}
                      {isWork && (
                        <>
                          <br />
                          Eingestempelt: {formatClockTime(entry.startTime)}
                          <br />
                          Ausgestempelt: {formatClockTime(entry.endTime)}
                          <br />
                          Pause: {Math.round((entry.pausedDuration || 0) / 60)} Minuten
                        </>
                      )}
                    </div>
                    <div className="entry-duration">{formatTime(duration)}</div>
                  </div>

                  {entry.adminEditedAt && (
                    <div className="small text-muted mt-2">
                      Bearbeitet von {getAdminEditorName(entry.adminEditedBy)} (Admin) am{' '}
                      {formatDateTime(entry.adminEditedAt)}
                      {entry.adminEditComment && (
                        <>
                          <br />
                          Kommentar:
                          {entry.adminEditComment.split('\n').map((line: string) => (
                            <React.Fragment key={line}>
                              <br />
                              {line}
                            </React.Fragment>
                          ))}
                        </>
                      )}
                    </div>
                  )}

                  {userRole !== 'admin' && pendingChangeRequest && (
                    <div className="small text-warning mt-2">
                      Offener Änderungsantrag vorhanden
                    </div>
                  )}

                  {(userRole === 'admin' || entry.userId === userId) && (
                    <div className="d-flex justify-content-end gap-2 mt-2">
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => handleEditClick(entry)}
                      >
                        {userRole === 'admin'
                          ? 'Bearbeiten'
                          : pendingChangeRequest
                          ? 'Antrag bearbeiten'
                          : 'Änderung beantragen'}
                      </button>
                      {userRole === 'admin' && (
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDelete(entry._id)}
                        >
                          Löschen
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showCreateModal && (
        <CreateTimeEntryForm
          allUsers={allUsers}
          initialUserId={selectedUserId}
          initialStartTime={getCreateTimeDefaults().startTime}
          initialEndTime={getCreateTimeDefaults().endTime}
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreate}
        />
      )}

      {showEditModal && entryToEdit && (
        <EditTimeEntryForm
          entry={entryToEdit}
          onClose={() => setShowEditModal(false)}
          onSave={handleSaveEdit}
          submitLabel={
            userRole === 'admin'
              ? 'Speichern'
              : entryToEdit.hasPendingChangeRequest
              ? 'Antrag speichern'
              : 'Antrag senden'
          }
          title={
            userRole === 'admin'
              ? 'Eintrag bearbeiten'
              : entryToEdit.hasPendingChangeRequest
              ? 'Änderungsantrag bearbeiten'
              : 'Zeitänderung beantragen'
          }
          confirmMessage={
            userRole === 'admin'
              ? 'Willst du diesen Eintrag wirklich speichern?'
              : 'Willst du diesen Änderungsantrag speichern?'
          }
        />
      )}

      {showSickForm && (
        <SickDayForm
          userId={selectedUserId}
          allUsers={allUsers}
          initialStartDate={getSelectedPeriodDates().startDate}
          initialEndDate={getSelectedPeriodDates().endDate}
          onClose={() => setShowSickForm(false)}
          onCreate={async (data) => {
            try {
              await dispatch(createSickDay(data)).unwrap();
              await dispatch(
                fetchEntries({
                  filter: activeTab,
                  date: selectedDate,
                  userId: userRole === 'admin' ? selectedUserId : userId,
                })
              );
              setShowSickForm(false);
            } catch {
              alert('Fehler beim Anlegen von Krankheitstagen');
            }
          }}
        />
      )}
    </>
  );
};

export default TimeEntryOverview;
